import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ArrowLeft, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";
import { getIcon } from "@/lib/icons";

const SWATCH = {
  cyber: "bg-brand-cyber text-white",
  pink: "bg-brand-pink text-white",
  tangerine: "bg-brand-tangerine text-brand-ink",
  lime: "bg-brand-lime text-brand-ink",
};

export default function MindMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);

  const load = async () => {
    setLoading(true); setErr(false);
    try {
      const { data } = await api.get("/universe/mindmap");
      setData(data);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background grid-paper">
        <Navbar />
        <div className="py-40 grid place-items-center" data-testid="mindmap-loading">
          <div className="grid place-items-center h-16 w-16 bg-brand-cyber text-white border border-border shadow-brutal">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-background grid-paper">
        <Navbar />
        <div className="max-w-md mx-auto px-4 pt-40 text-center" data-testid="mindmap-error">
          <p className="font-heading font-extrabold text-xl">Couldn't load the map</p>
          <p className="mt-2 text-sm text-muted-foreground">Please check your connection and try again.</p>
          <button onClick={load} className="mt-5 bg-brand-cyber text-white border border-border shadow-brutal-sm brutal-press px-4 py-2 font-heading font-bold text-sm">Retry</button>
        </div>
      </div>
    );
  }

  const branches = data.branches || [];
  const b = branches[active] || branches[0];
  const totalNodes = branches.reduce((n, x) => n + x.nodes.length, 0);

  return (
    <div className="min-h-screen bg-background grid-paper">
      <Navbar />
      <PageTransition className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <button data-testid="mindmap-back" onClick={() => navigate("/universe")} className="inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="h-4 w-4" /> AI Universe
        </button>

        {/* root node */}
        <div className="bg-brand-cyber text-white border border-border shadow-brutal p-6 md:p-8" data-testid="mindmap-root">
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-white/70">The master map</p>
          <h1 className="mt-2 font-heading font-extrabold tracking-tight text-3xl md:text-5xl leading-[1.05]">
            {data.root.label}
          </h1>
          <p className="mt-3 font-medium max-w-2xl text-white/90">{data.root.tagline}</p>
          <p className="mt-4 font-mono text-[11px] font-bold uppercase tracking-wide text-white/70">
            {branches.length} branches · {totalNodes} concepts · tap a branch to explore
          </p>
        </div>

        <div className="mt-8 lg:grid lg:grid-cols-12 lg:gap-6">
          {/* branch nav */}
          <nav className="lg:col-span-4" data-testid="mindmap-branches">
            <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar pb-2 lg:pb-0">
              {branches.map((br, i) => {
                const Icon = getIcon(br.icon);
                const on = i === active;
                return (
                  <button
                    key={br.id}
                    data-testid={`mindmap-branch-${br.id}`}
                    onClick={() => setActive(i)}
                    className={`shrink-0 lg:w-full text-left border border-border p-3 flex items-center gap-3 brutal-press transition-colors ${on ? `${SWATCH[br.color]} shadow-brutal-sm` : "bg-card hover:bg-secondary"}`}
                  >
                    <span className={`grid place-items-center h-8 w-8 shrink-0 border border-border ${on ? "bg-background/20" : SWATCH[br.color]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="font-heading font-bold text-sm leading-tight whitespace-nowrap lg:whitespace-normal">{br.label}</span>
                    <span className={`ml-auto hidden lg:inline font-mono text-[10px] font-bold ${on ? "opacity-70" : "text-muted-foreground"}`}>{br.nodes.length}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* branch detail */}
          <div className="lg:col-span-8 mt-6 lg:mt-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`mindmap-detail-${b.id}`}
              >
                <div className={`border border-border shadow-brutal-sm p-5 flex items-start gap-3 ${SWATCH[b.color]}`}>
                  <span className="grid place-items-center h-11 w-11 shrink-0 border border-border bg-background/20">
                    {(() => { const I = getIcon(b.icon); return <I className="h-5 w-5" />; })()}
                  </span>
                  <div>
                    <h2 className="font-heading font-extrabold tracking-tight text-2xl leading-tight">{b.label}</h2>
                    <p className="text-sm font-medium opacity-90">{b.summary}</p>
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {b.nodes.map((n, i) => (
                    <motion.div
                      key={n.label}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.05, 0.3) }}
                      className="bg-card border border-border shadow-brutal-sm p-4"
                    >
                      <div className="flex items-start gap-2">
                        <span className="grid place-items-center h-6 w-6 shrink-0 bg-foreground text-background font-heading font-black text-xs">{i + 1}</span>
                        <h4 className="font-heading font-extrabold text-sm leading-snug">{n.label}</h4>
                      </div>
                      <p className="mt-2 text-sm font-medium text-foreground/75 leading-relaxed">{n.detail}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 bg-secondary border border-dashed border-border p-4 flex items-center gap-3">
              <span className="grid place-items-center h-9 w-9 bg-brand-pink text-white border border-border"><Clock className="h-4 w-4" /></span>
              <p className="text-sm font-medium">
                Want the story in order? See the{" "}
                <button data-testid="mindmap-to-timeline" onClick={() => navigate("/universe/timeline")} className="font-bold underline underline-offset-2 hover:text-brand-pink">AI Timeline</button>
                {" "}— from 1950 to the future.
              </p>
            </div>
          </div>
        </div>
      </PageTransition>
    </div>
  );
}
